import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronLeft, ChevronRight, Bed, Bath, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useProperties } from '../hooks/useSanityData';
import { useLanguage } from '../contexts/LanguageContext';
import { Property } from '../types';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();
  const { properties, loading } = useProperties();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  // Get first 5 properties for the carousel
  const featuredProperties = properties.slice(0, 5);

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getTranslatedListingType = (listingType: string): string => {
    const key = listingType.toLowerCase().replace(' ', '');
    if (key === 'forsale') return t('listingTypes.forSale', 'For Sale');
    if (key === 'forrent') return t('listingTypes.forRent', 'For Rent');
    return listingType;
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % featuredProperties.length);
  }, [featuredProperties.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredProperties.length) % featuredProperties.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (featuredProperties.length === 0) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [featuredProperties.length, nextSlide]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(getLocalizedPath(`/properties?search=${encodeURIComponent(searchQuery)}`));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <div className="relative h-[640px] flex items-center justify-center">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/hero-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1
            className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight"
            style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.3)' }}
          >
            {t('homepage.tagline', 'Rentals. Homes. Agents. Loans.')}
          </h1>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t('homepage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  className="w-full h-16 pl-6 pr-16 text-lg rounded-lg shadow-xl border-0 focus:ring-0 bg-white"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white p-1 rounded-full">
                  <Button
                    type="submit"
                    size="icon"
                    className="h-10 w-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Featured Properties Carousel */}
      {featuredProperties.length > 0 && (
        <div className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('homepage.featured_properties', 'Featured Properties')}
              </h2>
              <p className="text-gray-600 text-lg">
                {t('homepage.featured_subtitle', 'Discover our handpicked selection of premium homes')}
              </p>
            </div>

            {/* Carousel */}
            <div className="relative">
              {/* Main carousel container */}
              <div className="overflow-hidden rounded-2xl">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {featuredProperties.map((property: Property) => (
                    <div key={property.id} className="w-full flex-shrink-0">
                      <Link to={getLocalizedPath(`/property/${property.id}`)} className="block">
                        <div className="relative h-[400px] md:h-[500px] group">
                          {/* Background Image */}
                          <img
                            src={property.mainImage}
                            alt={getTextInCurrentLanguage(property.title)}
                            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${property.sold ? 'opacity-75' : ''}`}
                          />
                          {property.sold && (
                            <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-center py-3 font-bold text-lg uppercase tracking-wide z-10">
                              {t('common.sold')}
                            </div>
                          )}
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                          {/* Content */}
                          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                            <div className="max-w-3xl">
                              {/* Badge */}
                              <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                                {getTranslatedListingType(property.listingType)}
                              </div>

                              {/* Price */}
                              <h3 className="text-3xl md:text-5xl font-bold text-white mb-3">
                                {formatPrice(property.price)}
                              </h3>

                              {/* Title */}
                              <h4 className="text-xl md:text-2xl font-semibold text-white/95 mb-2">
                                {getTextInCurrentLanguage(property.title)}
                              </h4>

                              {/* Address */}
                              <p className="text-white/80 text-base md:text-lg mb-4">
                                {property.address}, {property.city}, {property.state}
                              </p>

                              {/* Stats */}
                              <div className="flex items-center gap-6 text-white/90">
                                <div className="flex items-center gap-2">
                                  <Bed className="h-5 w-5" />
                                  <span className="font-medium">{property.beds} {t('common.beds', 'Beds')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Bath className="h-5 w-5" />
                                  <span className="font-medium">{property.baths} {t('common.baths', 'Baths')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Square className="h-5 w-5" />
                                  <span className="font-medium">{property.sqft.toLocaleString()} {t('common.sqft')}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label={t('homepage.previousSlide')}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label={t('homepage.nextSlide')}
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-6">
                {featuredProperties.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? 'w-8 bg-blue-600'
                        : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={t('homepage.goToSlide', { number: index + 1 })}
                  />
                ))}
              </div>
            </div>

            {/* View All Button */}
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate(getLocalizedPath('/properties'))}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 text-lg"
              >
                {t('homepage.view_all_properties', 'View All Properties')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Loading state for carousel */}
      {loading && (
        <div className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="h-10 w-64 bg-gray-200 rounded-lg mx-auto mb-3 animate-pulse"></div>
              <div className="h-6 w-96 bg-gray-200 rounded-lg mx-auto animate-pulse"></div>
            </div>
            <div className="h-[400px] bg-gray-200 rounded-2xl animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Action Cards Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Buy a home card */}
            <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 p-6 flex flex-col items-center text-center h-full">
              <div className="w-40 h-40 mb-6">
                <img src="/buy-home.png" alt={t('homepage.buy_home_title')} className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('homepage.buy_home_title')}</h3>
              <p className="text-gray-600 mb-8 flex-grow">
                {t('homepage.buy_home_description')}
              </p>
              <Button
                onClick={() => navigate(getLocalizedPath('/properties'))}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold px-6"
              >
                {t('homepage.browse_homes_button')}
              </Button>
            </div>

            {/* Rent a home card */}
            <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 p-6 flex flex-col items-center text-center h-full">
              <div className="w-40 h-40 mb-6">
                <img src="/rent-home.png" alt={t('homepage.rent_home_title')} className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('homepage.rent_home_title')}</h3>
              <p className="text-gray-600 mb-8 flex-grow">
                {t('homepage.rent_home_description')}
              </p>
              <Button
                onClick={() => navigate(getLocalizedPath('/properties?type=rent'))}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold px-6"
              >
                {t('homepage.find_rentals_button')}
              </Button>
            </div>

            {/* Sell a home card */}
            <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 p-6 flex flex-col items-center text-center h-full">
              <div className="w-40 h-40 mb-6">
                <img src="/sell-home.png" alt={t('homepage.sell_home_title')} className="w-full h-full object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('homepage.sell_home_title')}</h3>
              <p className="text-gray-600 mb-8 flex-grow">
                {t('homepage.sell_home_description')}
              </p>
              <Button
                onClick={() => navigate(getLocalizedPath('/properties'))}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold px-6"
              >
                {t('homepage.see_options_button')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Why Paraguay Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              {t('homepage.whyParaguay.eyebrow')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('homepage.whyParaguay.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('homepage.whyParaguay.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Low Prices Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {t('homepage.whyParaguay.lowPrices.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.whyParaguay.lowPrices.description')}
              </p>
            </div>

            {/* Tax Advantages Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {t('homepage.whyParaguay.taxAdvantages.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.whyParaguay.taxAdvantages.description')}
              </p>
            </div>

            {/* Stable Macro Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {t('homepage.whyParaguay.stableMacro.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.whyParaguay.stableMacro.description')}
              </p>
            </div>

            {/* Strategic Hub Card */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {t('homepage.whyParaguay.strategicHub.title')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('homepage.whyParaguay.strategicHub.description')}
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('homepage.whyParaguay.strategicHub.gdpGrowth')}:</span>
                  <span className="font-medium text-gray-900">4.2%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('homepage.whyParaguay.strategicHub.currency')}:</span>
                  <span className="font-medium text-gray-900">PYG</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('homepage.whyParaguay.strategicHub.stable')}:</span>
                  <span className="font-medium text-green-600">✓ {t('common.yes')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              {t('homepage.services.eyebrow')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('homepage.services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Private Investor Service */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('homepage.services.privateInvestor.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.services.privateInvestor.description')}
              </p>
            </div>

            {/* Residency Service */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('homepage.services.residency.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.services.residency.description')}
              </p>
            </div>

            {/* Legal Structuring Service */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('homepage.services.legalStructuring.title')}
              </h3>
              <p className="text-gray-600">
                {t('homepage.services.legalStructuring.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
